import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LayersIcon from '@mui/icons-material/Layers';
import PlaceIcon from '@mui/icons-material/Place';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { INVENTORY_COLORS } from '../../../../utils/inventory_helper';
import CompleteAddress, { defaultAddressShape } from '../../../../components/inventory/CompleteAddress';
import InventoryKeyHighlighter from '../../../../components/inventory/InventoryKeyHighlighter';
import PriceRateCard from './PriceRateCard';

const DEFAULT_CONSTRUCTION_STATUS_OPTIONS = [
  { value: 'pre_launch', label: 'Pre-Launch' },
  { value: 'new_launch', label: 'New Launch' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'near_possession', label: 'Near Possession' },
  { value: 'ready_to_move', label: 'Ready To Move' },
  { value: 'completed', label: 'Completed' },
];
const AREA_UNIT_OPTIONS = [
  { value: 'sqft', label: 'sqft' },
  { value: 'sqmt', label: 'sq.mt' },
  { value: 'acres', label: 'Acres' },
];

function formatINR(amount) {
  if (amount == null || isNaN(amount)) return '—';
  if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(2).replace(/\.?0+$/, '')} L`;
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function CardHeader({ title, onEdit, onSave, onCancel, isEditing, editLabel = 'Edit', saveLabel = 'Save', cancelLabel = 'Cancel', editStyle = 'green', icon, saveDisabled }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.75, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 1, fontSize: '0.6875rem', color: 'text.secondary' }}>
          {title}
        </Typography>
      </Box>
      {isEditing ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {onCancel && (
            <Box component="button" type="button" onClick={onCancel}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: 2.5, border: '1px solid', borderColor: 'grey.300', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', bgcolor: 'transparent', color: 'text.secondary', '&:hover': { bgcolor: 'grey.100' } }}>
              {cancelLabel}
            </Box>
          )}
          {onSave && (
            <Box component="button" type="button" onClick={onSave} disabled={saveDisabled}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: 2.5, border: 'none', fontSize: '0.75rem', fontWeight: 600, cursor: saveDisabled ? 'not-allowed' : 'pointer', bgcolor: INVENTORY_COLORS.green, color: '#fff', opacity: saveDisabled ? 0.7 : 1, '&:hover': saveDisabled ? {} : { bgcolor: INVENTORY_COLORS.greenMid } }}>
              {saveLabel}
            </Box>
          )}
        </Box>
      ) : (
        onEdit && (
          <Box component="button" type="button" onClick={onEdit}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: 2.5, border: '1px solid', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
              ...(editStyle === 'gold'
                ? { bgcolor: INVENTORY_COLORS.goldBg, borderColor: INVENTORY_COLORS.goldBorder, color: INVENTORY_COLORS.goldText, '&:hover': { bgcolor: '#f5e8c4' } }
                : editStyle === 'grey'
                  ? { bgcolor: 'grey.100', borderColor: 'grey.300', color: 'text.primary', '&:hover': { bgcolor: 'grey.200' } }
                  : editStyle === 'black'
                    ? { bgcolor: 'grey.900', borderColor: 'grey.700', color: '#fff', '&:hover': { bgcolor: 'grey.800' }, '& .MuiSvgIcon-root': { color: '#fff' } }
                    : { bgcolor: INVENTORY_COLORS.greenLight, borderColor: '#c5e4d0', color: INVENTORY_COLORS.green, '&:hover': { bgcolor: '#d0ecda' } }),
            }}>
            <EditIcon sx={{ fontSize: 14 }} />
            {editLabel}
          </Box>
        )
      )}
    </Box>
  );
}

const detailFieldStyles = {
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1 },
  '& .MuiInputLabel-root': { fontSize: 12 },
};

function getStaticMapEmbedUrl(lat, lon, zoom = 15) {
  if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const delta = 0.008;
  const bbox = [lon - delta, lat - delta, lon + delta, lat + delta].join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
}

// ── Left Column ───────────────────────────────────────────────────────────────
const LeftColumn = ({
  project,
  onEditDetails, onSaveDetails, onCancelDetails, isDetailsEditing, detailsSaving,
  onEditAddress, onSaveAddress, onCancelAddress, isAddressEditing, addressSaving,
  onEditTags, onSaveTags, onCancelTags, isTagsEditing, tagsSaving,
  onEditKeyHighlights, onSaveKeyHighlights, onCancelKeyHighlights, isKeyHighlightsEditing, keyHighlightsSaving,
  constructionStatusOptions = [], areaUnitOptions = [],
}) => {
  const constructionStatusOpts = constructionStatusOptions?.length ? constructionStatusOptions : DEFAULT_CONSTRUCTION_STATUS_OPTIONS;
  const areaUnitOpts = areaUnitOptions?.length ? areaUnitOptions : AREA_UNIT_OPTIONS;

  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [totalArea, setTotalArea] = useState('');
  const [areaUnit, setAreaUnit] = useState('sqft');
  const [launchDate, setLaunchDate] = useState(null);
  const [possessionDate, setPossessionDate] = useState(null);
  const [completionDate, setCompletionDate] = useState(null);
  const [rera, setRera] = useState('');
  const [reraApproved, setReraApproved] = useState(false);
  const [constructionStatus, setConstructionStatus] = useState('under_construction');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [tagsDraft, setTagsDraft] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [keyHighlightsDraft, setKeyHighlightsDraft] = useState(['']);
  const [addressEditDraft, setAddressEditDraft] = useState(() => ({ ...defaultAddressShape }));
  const prevTagsEditingRef = useRef(false);
  const prevKeyHighlightsEditingRef = useRef(false);

  useEffect(() => {
    if (isDetailsEditing && project) {
      setBudgetMin(project.price_min != null ? String(project.price_min) : '');
      setBudgetMax(project.price_max != null ? String(project.price_max) : '');
      setTotalArea(project.total_area != null ? String(project.total_area) : '');
      setAreaUnit(project.area_unit || 'sqft');
      const launchRaw = project.launch_date ?? '';
      setLaunchDate(launchRaw && dayjs(launchRaw).isValid() ? dayjs(launchRaw) : null);
      const posRaw = project.possession_date ?? project.possession ?? '';
      setPossessionDate(posRaw && dayjs(posRaw).isValid() ? dayjs(posRaw) : null);
      const compRaw = project.completion_date ?? '';
      setCompletionDate(compRaw && dayjs(compRaw).isValid() ? dayjs(compRaw) : null);
      setRera(project.rera ?? '');
      setReraApproved(Boolean(project.rera_approved));
      const statusValue = project.construction_status_value
        ?? (constructionStatusOpts.find((o) => o.label === project?.construction_status)?.value)
        ?? (constructionStatusOpts.find((o) => o.value === project?.construction_status_value)?.value)
        ?? (constructionStatusOpts[0]?.value)
        ?? 'under_construction';
      setConstructionStatus(statusValue);
      setDescriptionDraft(project.description ?? '');
    }
  }, [isDetailsEditing, project?.id]);

  useEffect(() => {
    const justEnteredEdit = isTagsEditing && !prevTagsEditingRef.current;
    prevTagsEditingRef.current = isTagsEditing;
    if (justEnteredEdit && project) {
      const raw = Array.isArray(project.tags) ? project.tags : [];
      setTagsDraft(raw.map((t) => (typeof t === 'string' ? t : (t?.label ?? String(t)))));
      setNewTagInput('');
    }
  }, [isTagsEditing, project?.id, project?.tags]);

  useEffect(() => {
    const justEnteredEdit = isKeyHighlightsEditing && !prevKeyHighlightsEditingRef.current;
    prevKeyHighlightsEditingRef.current = isKeyHighlightsEditing;
    if (justEnteredEdit && project) {
      const raw = Array.isArray(project.key_highlights) ? project.key_highlights : [];
      setKeyHighlightsDraft(raw.length > 0 ? raw.map((h) => String(h).trim()) : ['']);
    }
  }, [isKeyHighlightsEditing, project?.id, project?.key_highlights]);

  useEffect(() => {
    if (isAddressEditing && project) {
      const addr = project.address || {};
      setAddressEditDraft({
        ...defaultAddressShape,
        city: addr.city ?? project.city ?? '',
        state: addr.state ?? project.state ?? '',
        country: addr.country ?? project.country ?? 'IN',
        locality: addr.locality ?? '',
        address_line: addr.address_line ?? '',
        pincode: addr.pincode ?? '',
        latitude: addr.latitude ?? null,
        longitude: addr.longitude ?? null,
        google_maps_url: addr.google_maps_url ?? '',
        google_place_id: addr.google_place_id ?? '',
      });
    }
  }, [isAddressEditing, project?.id]);

  // Save: only primary Project model fields at top level; non-primary (e.g. rera_number) go in metadata
  const handleSaveDetails = () => {
    const meta = { ...(project?.metadata || {}) };
    if (rera.trim()) meta.rera_number = rera.trim();
    else if (meta.rera_number !== undefined) delete meta.rera_number;
    onSaveDetails?.({
      price_min: budgetMin ? parseFloat(budgetMin) : null,
      price_max: budgetMax ? parseFloat(budgetMax) : null,
      total_area: totalArea ? parseFloat(totalArea) : null,
      area_unit: areaUnit || 'sqft',
      construction_status: constructionStatus || 'under_construction',
      launch_date: launchDate && dayjs(launchDate).isValid() ? dayjs(launchDate).format('YYYY-MM-DD') : null,
      possession_date: possessionDate && dayjs(possessionDate).isValid() ? dayjs(possessionDate).format('YYYY-MM-DD') : null,
      completion_date: completionDate && dayjs(completionDate).isValid() ? dayjs(completionDate).format('YYYY-MM-DD') : null,
      rera_approved: reraApproved,
      description: (descriptionDraft || '').trim() || null,
      metadata: meta,
    });
  };

  const addTag = () => {
    const value = newTagInput.trim().toLowerCase();
    if (!value) return;
    if (tagsDraft.some((t) => String(t).toLowerCase() === value)) return;
    setTagsDraft((prev) => [...prev, value]);
    setNewTagInput('');
  };

  const removeTag = (index) => setTagsDraft((prev) => prev.filter((_, i) => i !== index));

  const handleSaveTags = () => {
    onSaveTags?.([...new Set(tagsDraft.map((t) => String(t).trim().toLowerCase()).filter(Boolean))]);
  };

  const handleSaveKeyHighlights = () => {
    const list = (keyHighlightsDraft || []).map((h) => String(h).trim()).filter(Boolean);
    onSaveKeyHighlights?.(list);
  };

  const handleSaveAddress = () => {
    const a = addressEditDraft;
    onSaveAddress?.({
      address: {
        city: (a.city || '').trim() || '',
        state: (a.state || '').trim() || '',
        country: (a.country || 'IN').trim() || 'IN',
        locality: (a.locality || '').trim() || '',
        address_line: (a.address_line || '').trim() || '',
        pincode: (a.pincode || '').trim() || '',
        latitude: a.latitude != null ? a.latitude : null,
        longitude: a.longitude != null ? a.longitude : null,
        google_place_id: (a.google_place_id || '').trim() || '',
        google_maps_url: (a.google_maps_url || '').trim() || '',
      },
    });
  };

  const tagsForDisplay = (() => {
    const list = Array.isArray(project?.tags)
      ? project.tags.filter((t) => t && !String(t).toLowerCase().includes('priority'))
      : [];
    if (list.length) return list.slice(0, 20);
    return [project?.category || 'Residential'].filter(Boolean);
  })();
  const tagToLabel = (t) => (typeof t === 'string' ? t : (t?.label ?? t));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* ── 1. Project Details ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <CardHeader
          title="Project Details"
          onEdit={onEditDetails}
          onSave={isDetailsEditing ? handleSaveDetails : undefined}
          onCancel={isDetailsEditing ? onCancelDetails : undefined}
          isEditing={isDetailsEditing}
          saveDisabled={detailsSaving}
          editStyle="grey"
          icon={
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HomeIcon sx={{ fontSize: 16, color: INVENTORY_COLORS.green }} />
            </Box>
          }
        />
        <Box sx={{ p: 2 }}>
          {isDetailsEditing ? (
            <>
              {/* 1. Construction Status — sets project context */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                  Construction Status
                </Typography>
                <FormControl fullWidth size="small" sx={detailFieldStyles}>
                  <Select
                    value={constructionStatus}
                    onChange={(e) => setConstructionStatus(e.target.value)}
                    displayEmpty
                    inputProps={{ id: 'construction-status-select' }}
                    renderValue={(v) => constructionStatusOpts.find((o) => o.value === v)?.label ?? v}
                  >
                    {constructionStatusOpts.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* 2. Pricing */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Price Min</Typography>
                  <TextField
                    fullWidth size="small" type="number"
                    value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)}
                    placeholder="e.g. 5000000" inputProps={{ min: 0 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                    sx={detailFieldStyles}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                    {budgetMin ? formatINR(parseFloat(budgetMin)) : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Price Max</Typography>
                  <TextField
                    fullWidth size="small" type="number"
                    value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)}
                    placeholder="e.g. 50000000" inputProps={{ min: 0 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                    sx={detailFieldStyles}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                    {budgetMax ? formatINR(parseFloat(budgetMax)) : '—'}
                  </Typography>
                </Box>
              </Box>

              {/* 3. Total Area + Unit + RERA toggle */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box sx={{ flex: '0 0 60%', minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Total Area</Typography>
                  <TextField
                    fullWidth size="small" type="number"
                    value={totalArea} onChange={(e) => setTotalArea(e.target.value)}
                    placeholder="e.g. 50000" inputProps={{ min: 0 }}
                    sx={detailFieldStyles}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                    {totalArea ? `${Number(totalArea).toLocaleString('en-IN')} ${areaUnitOpts.find((o) => o.value === areaUnit)?.label ?? areaUnit}` : '—'}
                  </Typography>
                </Box>

                {/* Area Unit */}
                <Box sx={{ flex: '0 0 calc(20% - 12px)', minWidth: 0, mt: '1.375rem' }}>
                  <FormControl fullWidth size="small" sx={detailFieldStyles}>
                    <Select
                      value={areaUnit}
                      onChange={(e) => setAreaUnit(e.target.value)}
                      displayEmpty
                      inputProps={{ id: 'area-unit-select' }}
                      renderValue={(v) => areaUnitOpts.find((o) => o.value === v)?.label ?? v}
                    >
                      {areaUnitOpts.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* RERA toggle button */}
                <Box
                  sx={{
                    flex: '0 0 calc(20% - 12px)', minWidth: 0, mt: '1.375rem',
                    height: 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    border: '1px solid',
                    borderColor: reraApproved ? `${INVENTORY_COLORS.green}60` : 'divider',
                    borderRadius: '6px',
                    bgcolor: reraApproved ? `${INVENTORY_COLORS.green}0D` : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                    px: 1,
                  }}
                  onClick={() => setReraApproved((v) => !v)}
                >
                  <Box sx={{
                    width: 15, height: 15, borderRadius: '3px', border: '1.5px solid', flexShrink: 0,
                    borderColor: reraApproved ? INVENTORY_COLORS.green : 'text.disabled',
                    bgcolor: reraApproved ? INVENTORY_COLORS.green : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}>
                    {reraApproved && (
                      <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </Box>
                  <Typography sx={{
                    fontSize: '0.6875rem', fontWeight: 600, whiteSpace: 'nowrap',
                    color: reraApproved ? INVENTORY_COLORS.green : 'text.secondary',
                    transition: 'color 0.15s ease',
                  }}>
                    RERA
                  </Typography>
                </Box>
              </Box>

              {/* RERA ID Input - shown when RERA is approved */}
              {reraApproved && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                    RERA ID / Number
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={rera}
                    onChange={(e) => setRera(e.target.value)}
                    placeholder="e.g. PRM/KA/RERA/1251/446/PR/171015/000977"
                    sx={detailFieldStyles}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                    Enter the official RERA registration number
                  </Typography>
                </Box>
              )}

              {/* 4. Dates — timeline, least urgently edited */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Launch Date</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker value={launchDate} onChange={(d) => setLaunchDate(d ?? null)} slotProps={{ textField: { fullWidth: true, size: 'small', sx: detailFieldStyles, placeholder: 'Launch date' } }} />
                  </LocalizationProvider>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Possession Date</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker value={possessionDate} onChange={(d) => setPossessionDate(d ?? null)} slotProps={{ textField: { fullWidth: true, size: 'small', sx: detailFieldStyles, placeholder: 'Possession date' } }} />
                  </LocalizationProvider>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Completion Date</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker value={completionDate} onChange={(d) => setCompletionDate(d ?? null)} slotProps={{ textField: { fullWidth: true, size: 'small', sx: detailFieldStyles, placeholder: 'Completion date' } }} />
                  </LocalizationProvider>
                </Box>
              </Box>

              {/* 5. Description */}
              <Box>
                <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Description</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  placeholder="Short project description"
                  size="small"
                  sx={{
                    ...detailFieldStyles,
                    '& .MuiInputBase-root': {
                      ...detailFieldStyles['& .MuiInputBase-root'],
                      height: 'auto',
                      maxHeight: 180,
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      overflow: 'auto',
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              {/* View mode */}

              {/* Construction Status — shown first for context */}
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '99', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: INVENTORY_COLORS.green + '18', flexShrink: 0 }}>
                  <CheckCircleIcon sx={{ fontSize: 17, color: INVENTORY_COLORS.green }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, lineHeight: 1, mb: 0.4 }}>Construction Status</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: INVENTORY_COLORS.green }}>
                    {project?.construction_status ?? project?.construction_status_display ?? project?.constructionStatus ?? project?.status ?? '—'}
                  </Typography>
                </Box>
              </Box>

              {/* Developer */}
              {(project?.developer || project?.developer_name) && (
                <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Developer</Typography>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>{project.developer || project.developer_name}</Typography>
                </Box>
              )}

              {/* Description, if any */}
              {(project?.description || '').trim() && (
                <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Description</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'text.primary', whiteSpace: 'pre-line' }}>
                    {project.description}
                  </Typography>
                </Box>
              )}

              {/* Detail grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, '& > .detail-cell': { p: 2, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: 'none' } }}>
                <Box className="detail-cell">
                  <Box sx={{ width: 36, height: 40, borderRadius: '20px', bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CurrencyRupeeIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600 }}>Pricing</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: INVENTORY_COLORS.green }}>{project?.budgetRange ?? '—'}</Typography>
                  </Box>
                </Box>

                <Box className="detail-cell">
                  <Box sx={{ width: 36, height: 40, borderRadius: '20px', bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ViewModuleIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600 }}>Total Area</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>{project?.totalAreaDisplay ?? project?.totalAreaRange ?? '—'}</Typography>
                  </Box>
                </Box>

                <Box className="detail-cell">
                  <Box sx={{ width: 36, height: 40, borderRadius: '20px', bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CalendarMonthIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600 }}>Launch Date</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>{project?.launch_date ?? '—'}</Typography>
                  </Box>
                </Box>

                <Box className="detail-cell">
                  <Box sx={{ width: 36, height: 40, borderRadius: '20px', bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ScheduleIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600 }}>Possession Date</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>{project?.possession_date ?? project?.possession ?? '—'}</Typography>
                  </Box>
                </Box>

                <Box className="detail-cell">
                  <Box sx={{ width: 36, height: 40, borderRadius: '20px', bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CalendarMonthIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600 }}>Completion Date</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>{project?.completion_date ?? '—'}</Typography>
                  </Box>
                </Box>

                <Box className="detail-cell">
                  <Box sx={{ width: 36, height: 40, borderRadius: '20px', bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <DescriptionIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600 }}>RERA Number</Typography>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, fontFamily: 'monospace', color: 'text.primary' }}>{project?.rera ?? '—'}</Typography>
                    {project?.rera_approved && (
                      <Chip size="small" label="RERA Approved" sx={{ mt: 0.5, alignSelf: 'flex-start', fontSize: '0.6875rem', height: 20, bgcolor: INVENTORY_COLORS.greenLight, color: INVENTORY_COLORS.green, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '99' }} />
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Card>

      {/* ── Key Highlights ────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <CardHeader
          title="Key Highlights"
          onEdit={onEditKeyHighlights}
          onSave={isKeyHighlightsEditing ? handleSaveKeyHighlights : undefined}
          onCancel={isKeyHighlightsEditing ? onCancelKeyHighlights : undefined}
          isEditing={isKeyHighlightsEditing}
          saveDisabled={keyHighlightsSaving}
          editStyle="grey"
          icon={<LayersIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
        />
        <Box sx={{ p: 2 }}>
          {isKeyHighlightsEditing ? (
            <InventoryKeyHighlighter
              value={keyHighlightsDraft?.length ? keyHighlightsDraft : ['']}
              onChange={setKeyHighlightsDraft}
              placeholder="e.g. 25+ years experience, Prime location"
              label=""
            />
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(Array.isArray(project?.key_highlights) ? project.key_highlights : []).length > 0
                  ? project.key_highlights.map((h, index) => (
                    <Chip
                      key={`${String(h)}-${index}`}
                      label={String(h)}
                      size="small"
                      sx={{ fontSize: '0.75rem', fontWeight: 500, py: 0.6, borderRadius: 2.5, bgcolor: INVENTORY_COLORS.greenLight, color: INVENTORY_COLORS.green, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '99', '& .MuiChip-label': { px: 1.5 } }}
                    />
                  ))
                  : (
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>No key highlights added.</Typography>
                  )}
              </Box>
              <Typography
                component="span"
                onClick={onEditKeyHighlights}
                sx={{ mt: 1.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', color: INVENTORY_COLORS.green, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                <AddIcon sx={{ fontSize: 18 }} /> Add Key Highlight
              </Typography>
            </>
          )}
        </Box>
      </Card>


      {/* ── 2. Tags — quick metadata scan ──────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <CardHeader
          title="Tags"
          onEdit={onEditTags}
          onSave={isTagsEditing ? handleSaveTags : undefined}
          onCancel={isTagsEditing ? onCancelTags : undefined}
          isEditing={isTagsEditing}
          saveDisabled={tagsSaving}
          editStyle="grey"
          icon={<LayersIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
        />
        <Box sx={{ p: 2 }}>
          {isTagsEditing ? (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {tagsDraft.map((tag, index) => (
                  <Chip
                    key={`${tagToLabel(tag)}-${index}`}
                    label={tagToLabel(tag)}
                    size="small"
                    onDelete={() => removeTag(index)}
                    sx={{ fontSize: '0.75rem', fontWeight: 500, py: 0.6, borderRadius: 2.5, bgcolor: INVENTORY_COLORS.greenLight, color: INVENTORY_COLORS.green, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '99', '& .MuiChip-label': { px: 1.5 }, '& .MuiChip-deleteIcon': { fontSize: 16 } }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="New tag (saved as lowercase)"
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  sx={{ ...detailFieldStyles, flex: '1 1 120px', minWidth: 140, '& .MuiInputBase-root': { height: 36 } }}
                />
                <Box
                  component="button" type="button"
                  onClick={addTag}
                  disabled={!newTagInput.trim()}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderRadius: 1.25, border: 'none', fontSize: '0.8125rem', fontWeight: 600, cursor: newTagInput.trim() ? 'pointer' : 'not-allowed', bgcolor: INVENTORY_COLORS.green, color: '#fff', opacity: newTagInput.trim() ? 1 : 0.6, '&:hover': newTagInput.trim() ? { bgcolor: INVENTORY_COLORS.greenMid } : {} }}
                >
                  <AddIcon sx={{ fontSize: 18 }} /> Add
                </Box>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Tags are stored as lowercase and unique. Press Enter or click Add to add.
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {tagsForDisplay.map((tag, index) => {
                  const label = tagToLabel(tag);
                  return (
                    <Chip
                      key={label && typeof label === 'string' ? `${label}-${index}` : index}
                      label={label}
                      size="small"
                      sx={{ fontSize: '0.75rem', fontWeight: 500, py: 0.6, borderRadius: 2.5, bgcolor: INVENTORY_COLORS.greenLight, color: INVENTORY_COLORS.green, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '99', '& .MuiChip-label': { px: 1.5 } }}
                    />
                  );
                })}
              </Box>
              <Typography
                component="span"
                onClick={onEditTags}
                sx={{ mt: 1.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', color: INVENTORY_COLORS.green, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                <AddIcon sx={{ fontSize: 18 }} /> Add Tag
              </Typography>
            </>
          )}
        </Box>
      </Card>


      {/* ── 3. Address — detailed, least urgently scanned ──────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <CardHeader
          title="Address"
          onEdit={onEditAddress}
          onSave={isAddressEditing ? handleSaveAddress : undefined}
          onCancel={isAddressEditing ? onCancelAddress : undefined}
          isEditing={isAddressEditing}
          saveDisabled={addressSaving || !(addressEditDraft.google_maps_url || '').trim()}
          editStyle="grey"
          icon={
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlaceIcon sx={{ fontSize: 16, color: INVENTORY_COLORS.green }} />
            </Box>
          }
        />
        <Box sx={{ p: 2 }}>
          {isAddressEditing ? (
            <>
              <CompleteAddress
                value={addressEditDraft}
                onChange={setAddressEditDraft}
                fieldStyles={detailFieldStyles}
                label="Search address or place"
                helperText="Select from map search to set location and show on map"
              />
              {(addressEditDraft.latitude != null && addressEditDraft.longitude != null) && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 1 }}>Map preview</Typography>
                  <Box sx={{ width: '100%', height: 200, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <iframe title="Address map" src={getStaticMapEmbedUrl(addressEditDraft.latitude, addressEditDraft.longitude)} width="100%" height="100%" style={{ border: 0 }} />
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {[
                  [project?.address?.address_line || project?.address?.locality, project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || project?.locality || project?.city || '—',
                  project?.address?.pincode ? `Pin: ${project.address.pincode}` : null,
                ].filter(Boolean).map((line, i) => (
                  <Typography key={i} sx={{ fontSize: '0.875rem', color: 'text.primary' }}>{line}</Typography>
                ))}
              </Box>
              {project?.address?.google_maps_url && (
                <Box sx={{ mt: 1.25 }}>
                  <Button
                    component="a"
                    href={project.address.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      borderRadius: 999,
                      px: 1.75,
                      borderColor: INVENTORY_COLORS.greenAccent,
                      color: INVENTORY_COLORS.green,
                      '&:hover': { borderColor: INVENTORY_COLORS.green, bgcolor: INVENTORY_COLORS.greenLight },
                    }}
                  >
                    Open in Google Maps →
                  </Button>
                </Box>
              )}
              {(project?.address?.latitude != null && project?.address?.longitude != null) && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ width: '100%', height: 160, borderRadius: 1.25, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <iframe title="Address map" src={getStaticMapEmbedUrl(project.address.latitude, project.address.longitude)} width="100%" height="100%" style={{ border: 0 }} />
                  </Box>
                  <Typography
                    component="a"
                    href={`https://www.openstreetmap.org/?mlat=${project.address.latitude}&mlon=${project.address.longitude}#map=17/${project.address.latitude}/${project.address.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '0.75rem', color: INVENTORY_COLORS.green, mt: 0.5, display: 'inline-block' }}
                  >
                    Open in OpenStreetMap →
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Card>

    </Box>
  );
};

// ── Revenue Card ──────────────────────────────────────────────────────────────
function formatCommissionOneDecimal(project) {
  const raw = project?.commission_rate != null
    ? Number(project.commission_rate)
    : (typeof project?.commission === 'string' ? parseFloat(project.commission) : null);
  if (raw == null || isNaN(raw)) return '—';
  return `${Number(raw).toFixed(1)}%`;
}

const RevenueCard = ({ project, onViewDetails }) => {
  const r = project?.revenue ?? {};
  const defaultRate = formatCommissionOneDecimal(project) === '—' ? '2.5%' : formatCommissionOneDecimal(project);
  const totalRevenue            = r.totalRevenue            ?? null;
  const totalCommission         = r.totalCommission         ?? 0;
  const totalCommissionReceived = r.totalCommissionReceived ?? 0;
  const totalCommissionPending  = r.totalCommissionPending  ?? Math.max(0, totalCommission - totalCommissionReceived);
  const receivedPct = totalCommission > 0 ? Math.round((totalCommissionReceived / totalCommission) * 100) : 0;
  const pendingPct  = 100 - receivedPct;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.75, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />
          <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 1, fontSize: '0.6875rem', color: 'text.primary' }}>Revenue</Typography>
        </Box>
        {onViewDetails && (
          <Box component="button" type="button" onClick={onViewDetails} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: 1.25, border: '1px solid', borderColor: 'grey.300', bgcolor: 'transparent', color: 'text.primary', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}>
            <EditIcon sx={{ fontSize: 14 }} /> View Details
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: INVENTORY_COLORS.green, color: '#fff' }}>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Total Revenue</Typography>
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '1.875rem', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>{formatINR(totalRevenue)}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', mt: 0.75 }}>Project lifetime value</Typography>
        </Box>
        <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: INVENTORY_COLORS.revenueOrange, color: '#fff' }}>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Total Commission</Typography>
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '1.875rem', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>{formatINR(totalCommission)}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', mt: 0.75 }}>Based on {defaultRate} commission rate</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          <Box sx={{ p: 1.75, borderRadius: 1.5, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '60' }}>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Received Commission</Typography>
            <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, color: INVENTORY_COLORS.green, lineHeight: 1.2 }}>{formatINR(totalCommissionReceived)}</Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.5 }}>{receivedPct}% received</Typography>
          </Box>
          <Box sx={{ p: 1.75, borderRadius: 1.5, bgcolor: 'rgba(234,88,12,0.08)', border: '1px solid', borderColor: 'rgba(234,88,12,0.25)' }}>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Pending Commission</Typography>
            <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, color: INVENTORY_COLORS.revenueOrange, lineHeight: 1.2 }}>{formatINR(totalCommissionPending)}</Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.5 }}>{pendingPct}% pending</Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

// ── Commission Card ───────────────────────────────────────────────────────────
const CommissionCard = ({ project, onEditCommission, onSaveCommission, onCancelCommission, isCommissionEditing = false, commissionSaving = false, onAddTier }) => {
  const defaultRateDisplay = formatCommissionOneDecimal(project);
  const [commissionDraft, setCommissionDraft] = useState('');

  useEffect(() => {
    if (isCommissionEditing && project) {
      const raw = project?.commission_rate != null ? Number(project.commission_rate) : null;
      setCommissionDraft(raw != null && !isNaN(raw) ? String(raw) : '');
    }
  }, [isCommissionEditing, project?.id]);

  const isValidDraft = (() => { const n = parseFloat(commissionDraft); return !isNaN(n) && n >= 0 && n <= 100; })();

  const handleSaveCommission = () => {
    const num = parseFloat(commissionDraft);
    if (isNaN(num)) return;
    onSaveCommission?.(Math.min(100, Math.max(0, num)));
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <CardHeader
        title="Commission Structure"
        onEdit={onEditCommission}
        onSave={isCommissionEditing ? handleSaveCommission : undefined}
        onCancel={isCommissionEditing ? onCancelCommission : undefined}
        isEditing={isCommissionEditing}
        saveDisabled={commissionSaving || !isValidDraft}
        editStyle="grey"
        icon={<CurrencyRupeeIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
      />
      <Box sx={{ mx: 2.75, mt: 2, mb: 0, p: 2.5, borderRadius: 1.5, background: `linear-gradient(135deg, ${INVENTORY_COLORS.green} 0%, ${INVENTORY_COLORS.greenMid} 100%)`, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -8, right: -8, width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', display: 'block', mb: 1 }}>Project Default Rate</Typography>
        {isCommissionEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <TextField
              type="number"
              value={commissionDraft}
              onChange={(e) => setCommissionDraft(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, sx: { bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, maxWidth: 140 } }}
              size="small"
              placeholder="0–100"
              error={commissionDraft !== '' && !isValidDraft}
              helperText={commissionDraft !== '' && !isValidDraft ? 'Enter 0–100' : ''}
              sx={{ '& .MuiInputBase-root': { color: 'grey.900' }, '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.9)' } }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)' }}>Percentage (0–100), e.g. 2.5</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '3.25rem', fontWeight: 700, color: '#fff', lineHeight: 1, letterSpacing: '-1px' }}>{defaultRateDisplay}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', mt: 1 }}>Applied to all configurations unless overridden</Typography>
          </>
        )}
      </Box>
      <Box sx={{ px: 2.75, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1.25, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '80' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenAccent, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={700} color="text.primary">Project Default</Typography>
              <Typography variant="caption" color="text.secondary">Applies to all configurations</Typography>
            </Box>
          </Box>
          <Typography variant="body1" fontWeight={700} sx={{ color: INVENTORY_COLORS.green }}>
            {isCommissionEditing && commissionDraft !== '' && isValidDraft ? `${Number(commissionDraft).toFixed(1)}%` : defaultRateDisplay}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1.25, bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={700} color="text.primary">Per Configuration</Typography>
              <Typography variant="caption" color="text.secondary">Overridable at config level</Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" fontStyle="italic">inherits default</Typography>
        </Box>
      </Box>
      <Box sx={{ mx: 2.75, mb: 2, p: 1.5, borderRadius: 1.25, bgcolor: '#fffbeb', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <WarningAmberIcon sx={{ fontSize: 20, color: '#ea580c', flexShrink: 0, mt: 0.25 }} />
        <Typography variant="caption" sx={{ color: '#92400e', lineHeight: 1.6 }}>
          Configuration-level rates override the project default. Set specific rates under <strong>Configurations</strong> to customize per unit type.
        </Typography>
      </Box>
    </Card>
  );
};

// ── Main ProjectOverview ──────────────────────────────────────────────────────
const ProjectOverview = ({
  project,
  showLeftColumn = true,
  onEditDetails,
  onSaveDetails,
  onCancelDetails,
  onEditAddress,
  onSaveAddress,
  onCancelAddress,
  isAddressEditing = false,
  addressSaving = false,
  onEditTags,
  onSaveTags,
  onCancelTags,
  onEditCommission,
  onSaveCommission,
  onCancelCommission,
  onAddTier,
  onNavigateTab,
  isDetailsEditing = false,
  detailsSaving = false,
  isTagsEditing = false,
  tagsSaving = false,
  onEditKeyHighlights,
  onSaveKeyHighlights,
  onCancelKeyHighlights,
  isKeyHighlightsEditing = false,
  keyHighlightsSaving = false,
  isCommissionEditing = false,
  commissionSaving = false,
  constructionStatusOptions = [],
  areaUnitOptions = [],
  propertyTypes = [],
  onSavePriceRateCard,
  priceRateCardSaving = false,
  brochureDownloadUrl,
  onUploadBrochure,
}) => {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: showLeftColumn ? { xs: '1fr', md: '1fr 420px' } : '1fr', gap: 2.5, alignItems: 'start' }}>
      {showLeftColumn && (
        <LeftColumn
          project={project}
          onEditDetails={onEditDetails}
          onSaveDetails={onSaveDetails}
          onCancelDetails={onCancelDetails}
          isDetailsEditing={isDetailsEditing}
          detailsSaving={detailsSaving}
          onEditAddress={onEditAddress}
          onSaveAddress={onSaveAddress}
          onCancelAddress={onCancelAddress}
          isAddressEditing={isAddressEditing}
          addressSaving={addressSaving}
          onEditTags={onEditTags}
          onSaveTags={onSaveTags}
          onCancelTags={onCancelTags}
          isTagsEditing={isTagsEditing}
          tagsSaving={tagsSaving}
          onEditKeyHighlights={onEditKeyHighlights}
          onSaveKeyHighlights={onSaveKeyHighlights}
          onCancelKeyHighlights={onCancelKeyHighlights}
          isKeyHighlightsEditing={isKeyHighlightsEditing}
          keyHighlightsSaving={keyHighlightsSaving}
          constructionStatusOptions={constructionStatusOptions}
          areaUnitOptions={areaUnitOptions}
        />
      )}

      {/* Right column: Brochure → Commission → Rate Card → Revenue */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Brochure: Upload or Download — above Commission */}
        {(brochureDownloadUrl != null || onUploadBrochure != null) && (
          <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.75, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PictureAsPdfIcon sx={{ fontSize: 16, color: INVENTORY_COLORS.green }} />
                </Box>
                <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 1, fontSize: '0.6875rem', color: 'text.secondary' }}>
                  Brochure
                </Typography>
              </Box>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
              {brochureDownloadUrl ? (
                <Button
                  component="a"
                  href={brochureDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  fullWidth
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
                    borderRadius: 1.5, borderColor: INVENTORY_COLORS.greenAccent,
                    color: INVENTORY_COLORS.green, '&:hover': { borderColor: INVENTORY_COLORS.green, bgcolor: INVENTORY_COLORS.greenLight },
                  }}
                >
                  Download
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  fullWidth
                  onClick={onUploadBrochure}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
                    borderRadius: 1.5, borderColor: INVENTORY_COLORS.greenAccent,
                    color: INVENTORY_COLORS.green, '&:hover': { borderColor: INVENTORY_COLORS.green, bgcolor: INVENTORY_COLORS.greenLight },
                  }}
                >
                  Upload Brochure
                </Button>
              )}
            </Box>
          </Card>
        )}
        <CommissionCard
          project={project}
          onEditCommission={onEditCommission}
          onSaveCommission={onSaveCommission}
          onCancelCommission={onCancelCommission}
          isCommissionEditing={isCommissionEditing}
          commissionSaving={commissionSaving}
          onAddTier={onAddTier}
        />
        <PriceRateCard
          project={project}
          onSave={onSavePriceRateCard}
          isSaving={priceRateCardSaving}
          propertyTypes={propertyTypes}
        />
        <RevenueCard project={project} onViewDetails={() => onNavigateTab?.('revenue')} />
      </Box>
    </Box>
  );
};

export default ProjectOverview;
